import {
  Context,
  CreateFetch,
  FetchResponse,
  NativeFetch,
  NormalizedOptionsFactory,
} from '../types';
import { isBrowser, resolveUrl } from '../utils';
import { invokeMiddlewares } from './invokeMiddlewares';
import { normalizeInit } from './normalizeInit';
import { normalizeOptions } from './normalizeOptions';

/**
 * Create the `Context` object for the current request.
 *
 * The context holds auxiliary information about the request that is to be
 * used by middlewares.
 */
function createContext(
  url: string,
  zapierBaseUrl: string,
  csrfTokenKey: string,
  fetcher: NativeFetch
): Context {
  return {
    url: resolveUrl(url, zapierBaseUrl),
    zapierBaseUrl,
    origin: isBrowser() ? location.origin : '',
    csrfTokenKey,
    isBrowser: isBrowser(),
    fetcher,
  };
}

/**
 * Create the `FetchResponse` object.
 *
 * `FetchResponse` exposes the most used attributes of `Response`. The original
 * response object is exposed by `FetchResponse#response` for advanced usage.
 */
function createResponse(response: Response): FetchResponse<any> {
  // Some `Response` properties are not enumerable so we explicitely copy them
  return {
    ok: response.ok,
    status: response.status,
    headers: response.headers,
    body: response.body,
    response,
  };
}

/**
 * Create a custom `fetch` function configured with `options`.
 *
 * The configured `fetch` function does the following internally:
 *
 *   1. Create the request's context for middlewares.
 *   2. Invoke request middlewares.
 *   3. Fetch the response using the provided `fetcher`.
 *   4. Invoke response middlewares.
 */
export const createFetch: CreateFetch = (options) => {
  const optionsFactory: NormalizedOptionsFactory = (url, init) =>
    normalizeOptions(
      typeof options === 'function' ? options(url, init) : options
    );

  return async function fetch(url, init = {}) {
    const { baseUrl, fetcher, beforeRequest, afterResponse, csrfTokenKey } =
      optionsFactory(url, init);

    const context = createContext(url, baseUrl, csrfTokenKey, fetcher);

    const finalInit = await invokeMiddlewares(
      beforeRequest,
      normalizeInit(init, csrfTokenKey),
      context,
      undefined
    );

    const res = await context.fetcher.call(
      null,
      context.url,
      finalInit as RequestInit
    );

    return await invokeMiddlewares(
      afterResponse,
      createResponse(res),
      context,
      finalInit
    );
  };
};
